import React, { Fragment, useMemo, useState } from 'react'
import { Dialog } from '../components/ui/dialog'
import { Table, TableProps } from '../components/ui/Table'
import { useSelector } from 'react-redux'
import { axiosGet } from '../util/api'
import { BreadcrumbProps } from '@blueprintjs/core'
import { Layout } from '../components/Layout'
import { LoadFunc, useTableHooks } from '../packages/blueprint-components/src/useTableHooks'
import { AuditLog } from '../components/audit_log/AuditLog'
import { RelativeTimestamp } from '../components/ui/RelativeTimestamp'
import { useParams } from 'react-router-dom'
import { AuditLogItem, AuditLogItemI } from '../components/audit_log/AuditLogItem'
import { Target } from '../components/audit_log/Target'
import { State } from '../reducers'
import { format, parseISO } from 'date-fns'
import { Column } from '../types/table'
import type { EntityId } from '../types/constants'

export const AuditLogDetail = ({ showHeader = true, nested = false }) => {
    const { log_id } = useParams()
    const [modal, setModal] = useState<AuditLog | null>(null)
    const [auditLog, setAuditLog] = useState<AuditLog | null>(null)

    const profile = useSelector<State, EntityId | null>((state) => state.user.profile)
    const load: LoadFunc<AuditLogItem> = (params: any) => {
        if (!profile) {
            return Promise.resolve()
        }

        return axiosGet(`audit-logs/${log_id}/browse`, params).then((res) => {
            if (res?.data?.data) {
                res.data.data = res.data.data.map(
                    (i: AuditLogItemI) =>
                        new AuditLogItem({
                            ...i,
                            audit_log: new AuditLog(res.data.audit_log),
                        }),
                )
                setAuditLog(new AuditLog(res.data.audit_log))
            }

            return Promise.resolve(res)
        })
    }
    const tableHooks = useTableHooks(load)

    const columns: Column<AuditLogItem, any>[] = useMemo(() => {
        return [
            {
                key: 'target_id',
                title: 'Item',
                cell: (log, { screenshotMode }: { screenshotMode: boolean }) => (
                    <Target auditLogItem={log} screenshotMode={screenshotMode} maxWidth={550} />
                ),
                width: 550,
                sortable: false,
            },
            {
                key: 'event',
                sortable: false,
                cell: (log) => <div>{log.event()}</div>,
            },
        ]
    }, [setModal])

    const tableOptions: TableProps<AuditLogItem, any> = {
        name: 'New Logs',
        showHeader: false,
        className: showHeader ? null : 'mt-0 mb-4',
        tableHooks: tableHooks,
        filters: null,
        dateSelect: false,
        columns: columns,
        onSelectChange: null,
        selectKey: 'campaign_id',
        columnSelection: false,
    }

    const breadcrumbs: BreadcrumbProps[] = [
        { text: 'Overview' },
        { href: '/combined-history', text: 'History' },
        { text: 'All other history' },
    ]

    function formatTimestamp(timestampStr?: string) {
        if (!timestampStr) {
            return '/'
        }

        const date = parseISO(timestampStr)
        return format(date, 'MMM d')
    }

    return (
        <Layout
            breadcrumbs={breadcrumbs}
            primary={
                <>
                    {formatTimestamp(auditLog?.created_at)}: {auditLog?.event()}
                </>
            }
            nested={nested}
        >
            <Table {...tableOptions}>
                {auditLog && (
                    <Fragment>
                        <div className="p-2">
                            Showing all {auditLog.meta?.count} changes to your{' '}
                            {auditLog.targetType()?.toLowerCase()}
                            {auditLog.meta?.count > 0 ? 's' : ''}
                            &nbsp;from the&nbsp;{auditLog.sourceType()} run{' '}
                            <RelativeTimestamp date={new Date(auditLog.created_at)} />.
                        </div>
                        <div className="p-2">{auditLog.event()}</div>
                    </Fragment>
                )}
            </Table>
            <Dialog isOpen={!!modal} onClose={() => setModal(null)} className="p-4">
                {modal && (
                    <Fragment>
                        <div className="">
                            <RelativeTimestamp
                                className="inline-block"
                                date={new Date(modal.created_at)}
                            />
                            <span>&nbsp;a&nbsp;</span>
                            <span>{modal.sourceType()}</span>
                            <span>&nbsp;{modal.event()}</span>
                            <span>&nbsp;on&nbsp;</span>
                            <span>{modal.effectedEntities()}.</span>
                        </div>
                        {JSON.stringify(modal)}
                    </Fragment>
                )}
            </Dialog>
        </Layout>
    )
}
